<template>
    <div>
        <CompassTable :isCheckable="false" class="unit-settings-table">
            <CompassTableHead>
                <CompassTableHeadCell v-if="editMode"></CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('settings.units.unit_list.activated') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell>
                    {{ $t.__('common.labels.value') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell v-if="showUnitCode">
                    {{ $t.__('common.labels.unit_code') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell class="unit-settings-table__head--centered">
                    {{ $t.__('common.labels.default_unit') }}
                </CompassTableHeadCell>
                <CompassTableHeadCell v-if="editMode"></CompassTableHeadCell>
            </CompassTableHead>
            <SaasDraggable
                v-model="localUnits"
                dragClass="unit-settings-table__row"
                :isTable="true"
                :disabled="!editMode"
                handle=".unit-settings-table__drag-handle"
                @update:model-value="handleSortUpdate"
            >
                <CompassTableRow
                    v-for="unitItem in units"
                    :key="`${unitItem.id}-${unit}`"
                    ref="table-row"
                    class="unit-settings-table__row"
                >
                    <CompassTableBodyCell
                        v-if="editMode"
                        :width="64"
                        class="unit-settings-table__fixed-width-cell"
                    >
                        <div class="unit-settings-table__drag-handle">
                            <CompassIcon
                                icon="drag-and-drop"
                                :type="isEditable ? 'primary' : 'disabled'"
                            />
                        </div>
                    </CompassTableBodyCell>
                    <CompassTableBodyCell
                        class="unit-settings-table__fixed-width-cell"
                        alignContent="center"
                    >
                        <template v-if="!editMode">
                            <CompassIcon
                                v-if="unitItem.is_active"
                                icon="success-circle-fill"
                                class="unit-settings-table__check-icon"
                            />
                        </template>
                        <CompassSwitch
                            v-else
                            v-model="unitItem.is_active"
                            class="unit-settings-table__switch"
                            orientation="left"
                        />
                    </CompassTableBodyCell>
                    <CompassTableBodyCell :width="220" maxWidth="220px">
                        <span v-if="!editMode" class="typographies-paragraph-medium-regular">
                            {{ unitItem.label }}
                        </span>
                        <CompassFormItem v-else label="" :error="getLabelError(unitItem)">
                            <CompassInput
                                v-model="unitItem.label"
                                :error="!!getLabelError(unitItem)"
                                @focus="unitFocused = unitItem.id"
                                @blur="getSuggestion(unitItem)"
                                @input="emptyCode(unitItem)"
                            />
                        </CompassFormItem>
                    </CompassTableBodyCell>
                    <CompassTableBodyCell v-if="showUnitCode">
                        <template v-if="!editMode">
                            <span
                                v-if="unitItem.parameters && unitItem.parameters.code"
                                class="typographies-paragraph-medium-regular"
                            >
                                {{ unitItem.parameters.code }}
                            </span>
                            <CompassTooltip
                                v-else
                                :content="$t.__('settings.units.unit_list.no_code.tooltip')"
                                :arrow="false"
                            >
                                <CompassIcon
                                    icon="warning-fill"
                                    class="unit-settings-table__warning-icon"
                                />
                            </CompassTooltip>
                        </template>
                        <div v-else class="unit-settings-table__suggestion-wrapper">
                            <CompassFormItem label="" :error="getCodeError(unitItem)">
                                <CompassInput
                                    v-model="unitItem.parameters.code"
                                    class="unit-settings-table__code-input"
                                    :error="!!getCodeError(unitItem)"
                                />
                            </CompassFormItem>
                            <template v-if="!unitItem.parameters.code">
                                <div
                                    v-if="showSuggestion(unitItem)"
                                    class="unit-settings-table__suggestion"
                                >
                                    <img
                                        src="/ressources/logged/img/icon/svg/wand.svg"
                                        height="20"
                                        width="20"
                                    />
                                    <span class="typographies-paragraph-medium-bold">
                                        {{ unitItem._embed.suggestion.code }}
                                    </span>
                                    <CompassButton
                                        :label="$t.__('common.actions.apply')"
                                        type="tertiary"
                                        size="small"
                                        @click="applySuggestion(unitItem.id)"
                                    />
                                </div>
                                <CompassLoader v-else-if="unitLoading === unitItem.id" />
                                <div
                                    v-else-if="unitFocused !== unitItem.id"
                                    class="unit-settings-table__suggestion--empty typographies-paragraph-medium-regular"
                                >
                                    {{ $t.__('settings.units.unit_list.no_suggestions') }}
                                </div>
                            </template>
                        </div>
                    </CompassTableBodyCell>
                    <CompassTableBodyCell
                        alignContent="center"
                        class="unit-settings-table__default-unit-cell"
                    >
                        <CompassTooltip
                            v-if="!editMode"
                            :content="$t.__('settings.units.unit_list.default_unit.tooltip')"
                            :disabled="!isNewUnit(unitItem.id)"
                            :arrow="false"
                        >
                            <CompassRadio
                                class="unit-settings-table__default-unit"
                                modelValue="Default unit"
                                :disabled="!canEditUnit || isNewUnit(unitItem.id)"
                                :checked="unit === unitItem.id"
                                @change="handleDefaultUnitUpdate(unitItem.id)"
                            />
                        </CompassTooltip>
                        <CompassIcon
                            v-if="editMode && unit === unitItem.id"
                            icon="success-circle-fill"
                            class="unit-settings-table__default-unit-check"
                        />
                    </CompassTableBodyCell>
                    <CompassTableBodyCell
                        v-if="editMode"
                        :width="64"
                        class="unit-settings-table__fixed-width-cell"
                    >
                        <CompassTooltip
                            :content="deleteButtonTooltipContent(unitItem.id)"
                            :disabled="isDeleteButtonEnabled(unitItem.id)"
                            :arrow="false"
                        >
                            <CompassIconButton
                                variant="destructive"
                                icon="bin"
                                :disabled="!isDeleteButtonEnabled(unitItem.id)"
                                @click="removeUnit(unitItem.id)"
                            />
                        </CompassTooltip>
                    </CompassTableBodyCell>
                </CompassTableRow>
            </SaasDraggable>
        </CompassTable>
        <AddUnitRow v-if="editMode" @addUnit="addUnit" />
    </div>
</template>
<script setup>
import { ref, watch, nextTick, useTemplateRef, inject } from 'vue';
import SaasDraggable from 'vcomponents/common/SaasDraggable.vue';
import { getUnitCodeSuggestions } from 'api/settings/unitService';
import AddUnitRow from 'vcomponents/preferences/Units/AddUnitRow.vue';

// Props
const props = defineProps({
    units: {
        type: Array,
        required: true,
    },
    canEditUnit: {
        type: Boolean,
        required: true,
    },
    isEditable: {
        type: Boolean,
        required: true,
    },
    unit: {
        type: Number,
        required: true,
    },
    editMode: {
        type: Boolean,
        required: true,
    },
    showUnitCode: {
        type: Boolean,
        default: false,
    },
});

// Emits
const emit = defineEmits(['sort', 'update:unit', 'removeUnit', 'addUnit']);

// Injected
const $t = inject('$t');

// Template refs
const tableRowRef = useTemplateRef('table-row');

// Reactive data
const localUnits = ref(props.units);
const unitLoading = ref(0);
const unitFocused = ref(0);

// Watchers
watch(
    () => props.units,
    (newValues) => {
        localUnits.value = newValues;
    },
);

// Methods
const isNewUnit = (unitId) => {
    return typeof unitId === 'string' && unitId.startsWith('temp-');
};

const isDeleteButtonEnabled = (unitId) => {
    return props.isEditable && props.unit !== unitId;
};

const deleteButtonTooltipContent = (unitId) => {
    if (!props.editMode) {
        return $t.__('settings.units.unit_list.delete_button.tooltip');
    }

    return props.unit === unitId
        ? $t.__('settings.units.unit_list.delete_button.tooltip.default_unit')
        : $t.__('settings.units.unit_list.delete_button.tooltip');
};

const handleDefaultUnitUpdate = (unitId) => {
    emit('update:unit', unitId);
};

const removeUnit = (unitId) => {
    emit('removeUnit', unitId);
};

const showSuggestion = (unitItem) => {
    return unitItem._embed.suggestion;
};

const applySuggestion = (unitId) => {
    const unit = props.units.find((unit) => unit.id === unitId);
    if (unit) {
        unit.parameters.code = unit._embed.suggestion.code;
        delete unit._embed.suggestion;
    }
};

const getSuggestion = (unit) => {
    if (!props.showUnitCode) {
        return;
    }
    if (unit.label && !unit.parameters.code) {
        unitLoading.value = unit.id;
        unitFocused.value = 0;
        getUnitCodeSuggestions([{ label: unit.label }])
            .then(({ data }) => {
                if (data.length && data[0].code) {
                    unit._embed.suggestion = data[0];
                }
            })
            .finally(() => {
                unitLoading.value = 0;
            });
    }
};

const addUnit = () => {
    emit('addUnit');
    nextTick(() => {
        const rows = tableRowRef.value;
        const newUnitRow = rows?.[rows.length - 1]?.$el.querySelector('.compass-input input');
        if (newUnitRow) {
            newUnitRow.focus();
        }
    });
};

const handleSortUpdate = (newOrder) => {
    emit('sort', newOrder);
};

const getLabelError = (unit) => {
    return getFieldError(unit, 'label');
};

const getCodeError = (unit) => {
    return getFieldError(unit, 'code');
};

const getFieldError = (unit, field) => {
    if (!unit._embed?.errors?.length) {
        return;
    }
    return unit._embed.errors.find((error) => error.field === field)?.message;
};

const emptyCode = (unitItem) => {
    unitItem.parameters.code = '';
    unitItem._embed.suggestion = null;
};
</script>
<style lang="scss">
.unit-settings-table__row.sortable-drag {
    .compass-table__cell-container {
        min-height: 56px;
        padding: var(--spacing-2) var(--spacing-3);
    }
}
</style>
<style lang="scss" scoped>
.unit-settings-table {
    border-bottom: 1px solid var(--colors-common-border-lighter);

    &__head {
        &--centered {
            :deep() {
                .compass-table__cell-tooltip {
                    justify-content: center;
                }
            }
        }
    }

    :deep() {
        .compass-table__cell-container,
        .compass-form-item {
            width: 100%;
        }

        .compass-loader {
            flex-shrink: 0;
        }
    }

    &__check-icon {
        color: var(--colors-common-icon-success);
    }

    &__default-unit {
        &-cell {
            white-space: nowrap;
            width: 1%;
        }

        &-check {
            color: var(--colors-common-icon-success);
        }

        :deep() {
            .compass-radio {
                gap: 0;

                &__label {
                    text-indent: -9999px;
                }
            }
        }
    }

    &__fixed-width-cell {
        white-space: nowrap;
        width: 1%;
    }

    &__drag-handle {
        align-items: center;
        display: flex;
        flex: 1;
        justify-content: center;
        min-height: 32px;
        width: 32px;
    }

    &__warning-icon {
        color: var(--colors-common-background-warning-default);
    }

    &__suggestion {
        align-items: center;
        color: var(--colors-common-text-subtle-default);
        display: flex;
        gap: var(--spacing-0);

        :deep(.compass-button) {
            margin: 0 0 0 var(--spacing-1);
        }

        &--empty {
            color: var(--colors-common-text-standard-caption);
        }

        &-wrapper {
            align-items: center;
            display: flex;
            gap: var(--spacing-3);
        }
    }

    &__code-input {
        width: 150px;
    }
}
</style>
